import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, FolderOpen, Loader2, Mic2, RotateCw, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { absUrl, errMsg, startTrailer } from '../../lib/aiStudioApi';
import { trailerRunning } from './useMovieProject';
import { useElapsed } from './LiveMedia';
import { ShareButton } from './FinalCutPanel';
import { cn } from '../../lib/utils';

const LENGTHS = [15, 20, 30];

function TrailerResult({ tr }) {
  return (
    <div className="space-y-3 duration-500 animate-in fade-in slide-in-from-bottom-2" data-testid="trailer-result">
      <video key={tr.url} src={absUrl(tr.url)} poster={absUrl(tr.thumb_url)} controls playsInline
        className="mx-auto max-h-[52vh] w-full rounded-xl border border-neutral-800 bg-black" data-testid="trailer-player" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-xs text-emerald-300" data-testid="trailer-done-step">
          {tr.step} · {tr.duration_seconds}s
          {tr.voice_lines > 0 && <span className="flex items-center gap-1 text-neutral-400"><Mic2 size={11} /> {tr.voice_lines} voice line{tr.voice_lines > 1 ? 's' : ''}</span>}
        </span>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline" className="border-neutral-700 text-neutral-200"><a href={absUrl(tr.url)} download data-testid="trailer-download-btn"><Download size={13} className="mr-1" /> Download</a></Button>
          {tr.library_item_id && <ShareButton itemId={tr.library_item_id} testId="trailer-share-btn" />}
          <Button asChild size="sm" variant="outline" className="border-neutral-700 text-neutral-200"><Link to="/studio-library" data-testid="trailer-open-library-btn"><FolderOpen size={13} className="mr-1" /> Library</Link></Button>
        </div>
      </div>
    </div>
  );
}

function TrailerStatus({ tr, running, onRetry }) {
  const elapsed = useElapsed(tr.started_at, running);
  if (running) return (
    <div className="space-y-2 rounded-xl border border-amber-400/30 bg-amber-500/5 p-4" data-testid="trailer-progress">
      <div className="flex justify-between text-sm"><span className="text-amber-100" data-testid="trailer-step">{tr.step || 'Picking the strongest moments'}</span><span className="font-mono text-neutral-300" data-testid="trailer-percent">{tr.progress || 0}%</span></div>
      <Progress value={tr.progress || 0} className="h-2 bg-neutral-800" />
      <div className="text-[11px] text-neutral-500">Elapsed {elapsed} · {tr.beats} beats · hard stop at 5:00</div>
    </div>
  );
  if (tr.status === 'failed') return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="trailer-error">
      <span className="text-sm text-red-200">{tr.error}</span>
      <Button size="sm" onClick={onRetry} className="bg-neutral-100 text-neutral-900 hover:bg-white" data-testid="trailer-retry-btn"><RotateCw size={13} className="mr-1" /> Retry</Button>
    </div>
  );
  return tr.status === 'completed' && tr.url ? <TrailerResult tr={tr} /> : null;
}

function LengthPicker({ value, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-neutral-800 bg-neutral-950 p-0.5" data-testid="trailer-length-picker">
      {LENGTHS.map((s) => (
        <button key={s} type="button" onClick={() => onChange(s)} data-testid={`trailer-length-${s}`}
          className={cn('rounded-full px-3 py-1 font-mono text-xs transition-colors duration-200', value === s ? 'bg-amber-500 text-neutral-900' : 'text-neutral-400 hover:text-neutral-200')}>
          {s}s
        </button>
      ))}
    </div>
  );
}

/** Free auto-trailer: the strongest keyframes as Living Stills (plus finished clips), taglines, title card and music. */
export function TrailerPanel({ project, onChanged }) {
  const series = project.format === 'series';
  const eps = series ? project.episodes_count || 1 : 1;
  const [episode, setEpisode] = useState(series ? 1 : 0);
  const [seconds, setSeconds] = useState(project.trailer?.seconds || 20);
  const [tags, setTags] = useState(project.trailer?.taglines?.length ? project.trailer.taglines : ['', '']);
  const [starting, setStarting] = useState(false);
  const tr = project.trailer || {};
  const running = trailerRunning(tr);
  const frames = (project.scenes || []).filter((s) => (!episode || (s.episode || 1) === episode) && (s.still_url || s.video_url)).length;
  const auto = project.trailer_taglines || [];

  async function start() {
    setStarting(true);
    try { await startTrailer(project.id, { episode, seconds, taglines: tags.filter((t) => t.trim()) }); toast.success('Cutting your trailer — free, on Konnect’s own server'); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setStarting(false); }
  }

  return (
    <div className="space-y-4 rounded-xl border border-neutral-800 bg-neutral-900/40 p-4" data-testid="trailer-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl space-y-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-100"><Sparkles size={15} className="text-amber-300" /> Trailer
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-200" data-testid="trailer-free-badge">FREE · 0 cr</span>
          </div>
          <p className="text-xs text-neutral-500">A {seconds}-second teaser from your strongest frames — Living Stills and finished clips, up to two voice lines, taglines, a title card and your music. No new AI renders.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <LengthPicker value={seconds} onChange={setSeconds} />
          {series && (
            <select value={episode} onChange={(e) => setEpisode(Number(e.target.value))} className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1.5 text-xs text-neutral-100" data-testid="trailer-episode-select">
              {Array.from({ length: eps }, (_, i) => <option key={i + 1} value={i + 1}>{`Episode ${i + 1}`}</option>)}
              <option value={0}>Season trailer</option>
            </select>
          )}
          <Button onClick={start} disabled={starting || running || frames < 3} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="trailer-start-btn">
            {starting || running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
            {running ? 'Cutting…' : tr.status === 'completed' ? 'Re-cut trailer' : 'Cut the trailer'}
          </Button>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <Input key={i} value={tags[i] || ''} maxLength={80} placeholder={auto[i] ? `Tagline ${i + 1} · auto: ${auto[i]}` : `Tagline ${i + 1} (optional)`}
            onChange={(e) => setTags((t) => Object.assign([...t], { [i]: e.target.value }))} data-testid={`trailer-tagline-${i + 1}`}
            className="h-8 border-neutral-800 bg-neutral-950 text-xs text-neutral-100 placeholder:text-neutral-600" />
        ))}
      </div>
      {frames < 3 && <div className="text-[11px] text-neutral-500" data-testid="trailer-needs-frames">Needs at least 3 finished storyboard frames — produce the storyboard first.</div>}
      <TrailerStatus tr={tr} running={running} onRetry={start} />
    </div>
  );
}
